// C:\Users\clint\f1-tipping-site\frontend\src\pages\AdminResultsPage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';

const AdminResultsPage = () => {
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [raceResults, setRaceResults] = useState(null);
  const [f1Results, setF1Results] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch available races
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        // Use the same endpoint as in Results.js
        const response = await apiClient.get('/api/f1data/races/all');
        
        // Filter only completed races
        const completedRaces = response.data.filter(race => race.status === 'completed');
        setRaces(completedRaces);
        
        if (completedRaces.length > 0) {
          // Select most recent completed race by default
          const mostRecentRace = completedRaces.sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          )[0];
          setSelectedRaceId(mostRecentRace._id);
        }
      } catch (err) {
        console.error('Error fetching races:', err);
        setError('Failed to load races');
      } finally {
        setLoading(false);
      }
    };

    fetchRaces();
  }, []);

  // Fetch race results when race selection changes
  useEffect(() => {
    if (!selectedRaceId) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Fetch race results
        const resultsResponse = await apiClient.get(`/api/scores/race/${selectedRaceId}`);
        
        console.log('Race results data:', resultsResponse.data);
        setRaceResults(resultsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedRaceId]);

  // Fetch F1 race results when race selection changes
  useEffect(() => {
    if (!selectedRaceId) return;
    
    const fetchF1Results = async () => {
      try {
        // Try to get results for this race
        const resultsResponse = await apiClient.get(`/api/results/race/${selectedRaceId}`);
        
        console.log('F1 race results data:', resultsResponse.data);
        setF1Results(resultsResponse.data);
      } catch (err) {
        console.error('Error fetching F1 results:', err);
        // Don't show error for this, just log it
      }
    };
    
    fetchF1Results();
  }, [selectedRaceId]);

  // Handle race selection change
  const handleRaceChange = (e) => {
    setSelectedRaceId(e.target.value);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Admin Results Explorer</h1>
        <Link to="/admin" className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
          Back to Admin
        </Link>
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">
          Select Race
        </label>
        <select
          value={selectedRaceId}
          onChange={handleRaceChange}
          className="w-full p-2 border rounded"
          disabled={loading}
        >
          <option value="">Select a race</option>
          {races.map(race => (
            <option key={race._id} value={race._id}>
              {race.season} {race.raceName} - Round {race.round}
            </option>
          ))}
        </select>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading data...</p>
        </div>
      ) : (
        <>
          {raceResults && (
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold mb-2">User Tipping Results</h2>
                <p className="text-gray-600 mb-4">This is the data for user predictions for the selected race.</p>
              </div>
              
              {/* Race Result Overview */}
              {raceResults.result && (
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold mb-4">Race Result Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {/* Basic Race Info */}
                    <div className="bg-gray-50 p-4 rounded border">
                      <h4 className="font-medium mb-2">Race Information</h4>
                      <ul className="space-y-1">
                        <li><strong>Season:</strong> {raceResults.result.season || 'N/A'}</li>
                        <li><strong>Round:</strong> {raceResults.result.round || 'N/A'}</li>
                        <li><strong>Date:</strong> {raceResults.result.date ? new Date(raceResults.result.date).toLocaleDateString() : 'N/A'}</li>
                      </ul>
                    </div>
                    
                    {/* Podium */}
                    {raceResults.result.podium && (
                      <div className="bg-gray-50 p-4 rounded border">
                        <h4 className="font-medium mb-2">Podium</h4>
                        <ul className="space-y-1">
                          {raceResults.result.podium.map((driver, index) => (
                            <li key={index}><strong>P{index + 1}:</strong> {driver}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Other Key Results */}
                    <div className="bg-gray-50 p-4 rounded border">
                      <h4 className="font-medium mb-2">Other Results</h4>
                      <ul className="space-y-1">
                        <li><strong>Pole Position:</strong> {raceResults.result.polePosition || 'N/A'}</li>
                        <li><strong>Fastest Lap:</strong> {raceResults.result.fastestLap || 'N/A'}</li>
                        <li><strong>First Retirement:</strong> {raceResults.result.firstRetirement || 'N/A'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Raw JSON Data */}
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Raw JSON Data</h3>
                <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96 text-xs">
                  {JSON.stringify(raceResults, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          {/* F1 Race Results Section */}
          {f1Results && (
                <div className="bg-white shadow-md rounded-lg overflow-hidden mt-6">
                    <div className="p-6 border-b flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold mb-2">Formula 1 Race Results</h2>
                        <p className="text-gray-600">Official F1 race results for this event.</p>
                    </div>
                    <button 
                        onClick={async () => {
                            try {
                              setLoading(true);
                              console.log("Starting F1 data refresh...");
                              
                              // Call API endpoint to fetch fresh data from Ergast
                              console.log("Calling /api/results/fetch-from-api/...");
                              const fetchResponse = await apiClient.post(`/api/results/fetch-from-api/${selectedRaceId}`);
                              console.log("API response:", fetchResponse.data);
                              
                              // Then refresh our local data
                              console.log("Refreshing local data...");
                              const refreshedData = await apiClient.get(`/api/results/race/${selectedRaceId}`);
                              console.log("Refreshed data:", refreshedData.data);
                              
                              setF1Results(refreshedData.data);
                              setLoading(false);
                              console.log("Refresh complete!");
                            } catch (err) {
                              console.error('Error refreshing F1 data:', err);
                              setError('Failed to refresh F1 data');
                              setLoading(false);
                            }
                          }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    >
                        Refresh F1 Data
                    </button>
                    </div>
                
                <div className="p-6">
                {f1Results.results && f1Results.results.length > 0 ? (
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pos</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Starting Grid</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Points</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fastest Lap</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {f1Results.results.map((driver, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">{driver.position}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                                <div className="font-medium">{driver.givenName} {driver.familyName}</div>
                                <div className="text-xs text-gray-500">{driver.code}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">{driver.constructorName}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">{driver.grid}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">{driver.status}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">{driver.points}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {driver.fastestLap?.rank === '1' ? (
                                <span className="text-purple-600 font-medium">
                                    {driver.fastestLap.time} 
                                    <span className="ml-1 text-xs">
                                    (Lap {driver.fastestLap.lap})
                                    </span>
                                </span>
                                ) : '—'}
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                ) : (
                    <div>
                    {/* Show the key race results if detailed results aren't available */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-gray-50 p-4 rounded border">
                        <h4 className="font-medium mb-2">Pole Position</h4>
                        <p>{f1Results.polePosition || 'N/A'}</p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded border">
                        <h4 className="font-medium mb-2">Fastest Lap</h4>
                        <p>{f1Results.fastestLap || 'N/A'}</p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded border">
                        <h4 className="font-medium mb-2">First Retirement</h4>
                        <p>{f1Results.firstRetirement || 'N/A'}</p>
                        </div>
                    </div>
                    
                    <p className="text-gray-500 mt-4">No detailed driver results available for this race.</p>
                    </div>
                )}
                </div>
            </div>
            )}
        </>
      )}
    </div>
  );
};

export default AdminResultsPage;